import * as React from "react";
import { Trans } from "@lingui/react/macro";
import { Button } from "../../../components/ui/button";
import { PauseCircleIcon, PlayCircleIcon, RotateCcwIcon, SquareIcon } from "lucide-react";
import { useTimer } from "../../../context/TimerContext";
import { formatTime } from "../../../lib/time";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../../components/ui/alert-dialog";
import { format } from "date-fns";
import { useAuth } from "../../../context/AuthProvider";
import axios from "../../../lib/axios";
import { Console } from "console";
import { toast } from "sonner";

const StartDriveButton: React.FC<{ id?: number, disabled?: boolean | undefined }> = ({ id, disabled }) => {
    const { setId, setIsRunning } = useTimer('home');
    return (
        <Button
            className="w-full"
            onClick={() => {
                setId(id != null ? String(id) : "0");
                setIsRunning(true);
            }}
            disabled={disabled}
        >
            Start thuisrit
        </Button>
    );
};

const RunningTimer = ({
    size = "lg",
    className = "",
}: { size?: "sm" | "md" | "lg"; className?: string }) => {
    const { isRunning, setIsRunning, elapsedTime } = useTimer('home');
    const S = {
        sm: { wrap: "gap-2 p-1", icon: "h-5 w-5", text: "text-lg", padText: "px-1" },
        md: { wrap: "gap-3 p-2", icon: "h-6 w-6", text: "text-xl", padText: "px-2" },
        lg: { wrap: "gap-4 p-3", icon: "h-8 w-8", text: "text-3xl", padText: "px-3" },
    }[size];

    return (
        <div className={`flex items-center ${S.wrap} bg-white border rounded-full shadow-sm ${className}`}>
            {isRunning ? (
                <button onClick={() => setIsRunning(false)} className="text-orange-500" aria-label="Pauzeer">
                    <PauseCircleIcon className={S.icon} />
                </button>
            ) : (
                <button onClick={() => setIsRunning(true)} className="text-green-600" aria-label="Hervat">
                    <PlayCircleIcon className={S.icon} />
                </button>
            )}
            <span className={`tabular-nums font-semibold tracking-tight leading-none ${S.text} ${S.padText}`}>
                {formatTime(elapsedTime)}
            </span>
        </div>
    );
};


type EndPayload = { id?: Number; startTime: Date | null; };
// Confirmed End: force id = "0" for home drive
export const EndDriveConfirmButton = ({ onEnd }: { onEnd: (p: EndPayload) => Promise<boolean> }) => {
    const { startTime, elapsedTime, resetTimer, id } = useTimer('home');
    const [open, setOpen] = React.useState(false);
    const [busy, setBusy] = React.useState(false);

    const interventionid = (() => {
        const n = Number(id);
        return Number.isFinite(n) ? n : 0;
    })();

    const confirmEnd = async () => {
        setBusy(true);
        try {
            const ok = await onEnd({ id: interventionid, startTime });
            if (ok) {
                resetTimer();
                setOpen(false);
            } else {
                toast.error("Kon thuisrit niet opslaan. Probeer opnieuw.");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button disabled={busy}>
                    <SquareIcon className="h-4 w-4" />
                    <span className="ml-2">Beëindigen rit</span>
                </Button>
            </AlertDialogTrigger>

            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Rit beëindigen?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Dit zal de rit opslaan en stoppen. Tijdsduur: {formatTime(elapsedTime)}.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy}>Annuleren</AlertDialogCancel>

                    <AlertDialogAction asChild>
                        <Button onClick={confirmEnd} disabled={busy}>
                            {busy ? "Bezig..." : "Bevestigen"}
                        </Button>
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

// Reset
export const ResetDriveButton: React.FC = () => {
    const { resetTimer } = useTimer('home');
    return (
        <Button
            size="sm"
            variant="ghost"
            className="inline-flex items-center gap-1"
            onClick={resetTimer}
        >
            <RotateCcwIcon className="h-4 w-4" />
            <Trans>Reset</Trans>
        </Button>
    );
};

export const StartEndHomeDrive: React.FC<{ interventionId?: number, disabled?: boolean | undefined }> = ({ interventionId, disabled }) => {
    const { isRunning, startTime, elapsedTime, resetTimer } = useTimer('home');
    const hasStarted = isRunning || !!startTime || elapsedTime > 0;
    const { onEndHomeDrive } = useHomeDriveHandlers();

    return (
        <>
            {!hasStarted ? <StartDriveButton id={interventionId} disabled={disabled} /> :
                <div className="flex flex-col w-full gap-4">
                    <EndDriveConfirmButton onEnd={onEndHomeDrive} />
                    <Button className="w-full" onClick={resetTimer}>
                        <RotateCcwIcon className="h-4 w-4" /> Reset
                    </Button>
                    <RunningTimer size="lg" className="mx-auto" />
                </div>
            }
        </>
    );
};


function useHomeDriveHandlers() {
    const { auth } = useAuth();
    const technicianId = auth.data?.databases[0]?.technicianid;
    const dbId = auth.data?.databases[0]?.dbid;

    const onEndHomeDrive = async ({ startTime, id }: EndPayload): Promise<boolean> => {
        try {
            const s = startTime ?? new Date();
            const end = new Date();

            const homeTrip = {
                triptimeid: 0,
                technicianid: Number(technicianId ?? 0),
                interventionid: Number(id ?? 0),
                date: format(s, "yyyyMMdd"),
                starttime: format(s, "HH:mm:ss"),
                endtime: format(end, "HH:mm:ss"),
                duration: "0",
                activity: "HOME",
                timestamp: format(end, "yyyyMMddHHmmss"),
            };

            await axios.post(`triptime?dbid=${dbId}`, { triptimes: [homeTrip] });
            //console.warn(JSON.stringify({ triptimes: [homeTrip] }, null, 2));
            const key = `homeRideDone:${dbId}:${technicianId}:${format(end, "yyyyMMdd")}`;
            localStorage.setItem(key, "1");

        } catch (err) {
            console.error(err);
            //toast.error("Netwerkfout: kon thuisrit niet opslaan.");
            return false;
        }

        window.dispatchEvent(new Event("homeRideDone"));
        return true;
    };

    return { onEndHomeDrive };
}
